import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableEnergyStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportenergyStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchEnergyReportStation,
  updateReportFilters,
  fetchListFilters,
  setEnergyStationReportUpdate,
  setReportFiltersData,
  setSortData,
} from '../../../../redux/slices/ReportEnergyStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * Reports management page
 */
function EnergyReportTableStation(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isEnergyStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportEnergyStation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableEnergyStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableEnergyStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableEnergyStationReport,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableEnergyStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role='presentation' >
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role='presentation'>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),

    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableEnergyStationReport,
      sortAllow: true,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableEnergyStationReport) => (
        <span>{ToFixedValue(row?.powerConsumed)}</span>
      ),
    },
    {
      label: t('basePricingEnergyConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableEnergyStationReport) => (
        <span>{ToFixedValue(row?.basePricePowerConsumed)}</span>
      ),
    },

    {
      label: t('peakPricingEnergyConsumption'),
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableEnergyStationReport) => (
        <span>{ToFixedValue(row?.peakPricePowerConsumed)}</span>
      ),
    },
    {
      label: t('offPeakPricingEnergyConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableEnergyStationReport) => (
        <span>{ToFixedValue(row?.offPeakPricePowerConsumed)}</span>
      ),
    },
    {
      label: t('scheduleChargeEnergyConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableEnergyStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param row
       * @returns
       */
      customData: (row: AvailableEnergyStationReport) => (
        <span>{ToFixedValue(row?.scheduledPowerConsumed)}</span>
      ),
    },
  ];
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchEnergyReportStation({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
    });
  };

  /**
   * Current columns for Energy Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('basePricingEnergyConsumption'), accessorKey: 'basePricePowerConsumed' },
    { label: t('peakPricingEnergyConsumption'), accessorKey: 'peakPricePowerConsumed' },
    { label: t('offPeakPricingEnergyConsumption'), accessorKey: 'offPeakPricePowerConsumed' },
    { label: t('scheduleChargeEnergyConsumption'), accessorKey: 'scheduledPowerConsumed' },
  ];
  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchEnergicStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableEnergyStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'powerConsumed':
                return row?.powerConsumed ? ToFixedValue(row.powerConsumed) : '0.00';

              case 'basePricePowerConsumed':
                return row?.basePricePowerConsumed
                  ? ToFixedValue(row.basePricePowerConsumed)
                  : '0.00';

              case 'peakPricePowerConsumed':
                return row?.peakPricePowerConsumed
                  ? ToFixedValue(row.peakPricePowerConsumed)
                  : '0.00';

              case 'offPeakPricePowerConsumed':
                return row?.offPeakPricePowerConsumed
                  ? ToFixedValue(row.offPeakPricePowerConsumed)
                  : '0.00';

              case 'scheduledPowerConsumed':
                return row?.scheduledPowerConsumed
                  ? ToFixedValue(row.scheduledPowerConsumed)
                  : '0.00';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param value
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    if (reportsData?.accountId) {
      fetchData({
        ...GetParametersFromCommonFilters(filters),
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        ...value,
        sortBy,
        sortField,
      });
    }
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (reportsData?.accountId) {
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });
    }
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isEnergyStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setEnergyStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isEnergyStationReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'energyReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={window.innerHeight >= 700 ? 320 : 339}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          ReferenceCurrentPages={ReferenceCurrentPages}
          sizeChangeButtonRequired
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
    </div>
  );
}

export default EnergyReportTableStation;
